import React from 'react';
import { Box, Button, Divider, IconButton, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { Add, ArrowBackRounded, ArrowForwardRounded, CheckCircleRounded, Remove, RocketLaunchRounded } from '@mui/icons-material';

export const launchActionButtonSx = {
  minHeight: 52,
  borderRadius: 3,
  px: 3,
  fontWeight: 900,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} as const;

export const QuestionCard: React.FC<React.PropsWithChildren<{
  eyebrow?: string;
  title: string;
  detail?: string;
  progress?: { current: number; total: number };
}>> = ({ eyebrow, title, detail, progress, children }) => (
  <Paper elevation={0} sx={{
    p: { xs: 2, sm: 3.5, md: 5 }, width: '100%', maxWidth: 860, mx: 'auto',
    minHeight: { xs: 'auto', sm: 540 }, borderRadius: { xs: 4, sm: 5 },
    border: '1px solid', borderColor: 'rgba(15, 23, 42, 0.08)',
    boxShadow: { xs: '0 12px 36px rgba(15, 23, 42, 0.08)', sm: '0 24px 70px rgba(15, 23, 42, 0.10)' },
    overflow: 'hidden', position: 'relative',
    '&::before': { content: '""', position: 'absolute', inset: '0 0 auto', height: 5,
      background: 'linear-gradient(90deg, #1976d2 0%, #44a3ff 52%, #74d4ff 100%)' },
  }}>
    <Stack spacing={{ xs: 2, sm: 3.5 }} sx={{ minHeight: { sm: 445 } }}>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
          sx={{ mb: { xs: progress ? .75 : 0, sm: 1.5 }, display: { xs: progress ? 'flex' : 'none', sm: 'flex' } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 2.25, display: 'grid', placeItems: 'center',
              bgcolor: 'rgba(25,118,210,.10)', color: 'primary.main' }}>
              <RocketLaunchRounded sx={{ fontSize: 19 }} />
            </Box>
            <Typography variant="overline" color="primary.main" fontWeight={900} letterSpacing=".09em">
              {eyebrow ?? 'Quick setup'}
            </Typography>
          </Stack>
          {progress ? <Typography variant="caption" fontWeight={850} color="text.secondary">
            {progress.current + 1} / {progress.total}
          </Typography> : null}
        </Stack>
        {progress ? <LinearProgress variant="determinate"
          value={Math.min(100, ((progress.current + 1) / Math.max(progress.total, 1)) * 100)}
          aria-label={`Step ${progress.current + 1} of ${progress.total}`}
          sx={{ height: { xs: 6, sm: 8 }, borderRadius: 999, mb: { xs: 2, sm: 3.5 }, bgcolor: '#e7edf5',
            '& .MuiLinearProgress-bar': { borderRadius: 999, background: 'linear-gradient(90deg, #1976d2, #51adff)', transition: 'transform 300ms ease' } }} /> : null}
        <Typography variant="h4" component="h2" fontWeight={900} sx={{
          fontSize: { xs: '1.45rem', sm: '2.2rem' }, lineHeight: 1.15, letterSpacing: '-.025em', maxWidth: 720,
        }}>{title}</Typography>
        {detail ? <Typography color="text.secondary" sx={{ mt: { xs: .75, sm: 1.25 }, fontSize: { xs: '.92rem', sm: '1.05rem' }, lineHeight: { xs: 1.5, sm: 1.65 }, maxWidth: 680 }}>{detail}</Typography> : null}
      </Box>
      <Stack spacing={3} sx={{ flex: 1, justifyContent: 'space-between',
        '& .MuiTextField-root': { width: '100%' },
        '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fbfdff' },
      }}>{children}</Stack>
    </Stack>
  </Paper>
);

export const NumericStepper: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}> = ({ label, value, min, max, step = 1, onChange }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2}
    sx={{ p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 3.5, bgcolor: '#fbfdff' }}>
    <Typography fontWeight={850} fontSize="1.05rem">{label}</Typography>
    <Stack direction="row" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }} spacing={1.5}>
      <IconButton aria-label={`Decrease ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - step))}
        sx={{ width: 48, height: 48, border: '2px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Remove />
      </IconButton>
      <Typography aria-live="polite" variant="h4" fontWeight={900} sx={{ minWidth: 64, textAlign: 'center' }}>{value}</Typography>
      <IconButton aria-label={`Increase ${label}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + step))}
        sx={{ width: 48, height: 48, border: '2px solid', borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(25,118,210,.06)' }}>
        <Add />
      </IconButton>
    </Stack>
  </Stack>
);

export const ReviewSummary: React.FC<{ rows: Array<{ label: string; value: React.ReactNode }> }> = ({ rows }) => (
  <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
    {rows.map((row, index) => (
      <React.Fragment key={row.label}>
        {index ? <Divider /> : null}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ p: { xs: 1.4, sm: 2 } }}>
          <Typography color="text.secondary" sx={{ fontSize: { xs: '.9rem', sm: '1rem' } }}>{row.label}</Typography>
          <Typography fontWeight={800} textAlign="right" sx={{ fontSize: { xs: '.9rem', sm: '1rem' } }}>{row.value}</Typography>
        </Stack>
      </React.Fragment>
    ))}
  </Paper>
);

export const CompletedStepCard: React.FC<React.PropsWithChildren<{
  eyebrow: string;
  title: string;
  detail: string;
}>> = ({ eyebrow, title, detail, children }) => (
  <Paper elevation={0} sx={{
    width: '100%', maxWidth: 860, mx: 'auto', p: { xs: 2.5, sm: 4, md: 5 },
    borderRadius: { xs: 4, sm: 5 }, border: '1px solid rgba(25,118,210,.14)',
    background: 'linear-gradient(145deg, #ffffff 0%, #f7fbff 100%)',
    boxShadow: '0 22px 60px rgba(15,23,42,.09)', overflow: 'hidden', position: 'relative',
    '&::before': { content: '""', position: 'absolute', inset: '0 0 auto', height: 5,
      background: 'linear-gradient(90deg, #1976d2 0%, #44a3ff 52%, #74d4ff 100%)' },
  }}>
    <Stack spacing={{ xs: 2.5, sm: 3.5 }}>
      <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
        <Box sx={{ width: { xs: 46, sm: 54 }, height: { xs: 46, sm: 54 }, flex: '0 0 auto', borderRadius: 3,
          display: 'grid', placeItems: 'center', bgcolor: '#e8f7ef', color: 'success.main' }}>
          <CheckCircleRounded sx={{ fontSize: { xs: 27, sm: 32 } }} />
        </Box>
        <Box>
          <Typography variant="overline" color="primary.main" fontWeight={900} letterSpacing=".09em">{eyebrow}</Typography>
          <Typography variant="h4" component="h2" fontWeight={900} sx={{
            mt: .15, fontSize: { xs: '1.45rem', sm: '2rem' }, lineHeight: 1.15, letterSpacing: '-.025em',
          }}>{title}</Typography>
          <Typography color="text.secondary" sx={{ mt: .75, lineHeight: 1.6 }}>{detail}</Typography>
        </Box>
      </Stack>
      {children}
    </Stack>
  </Paper>
);

// Steps whose choices live in the card body still render this bar, without a
// Continue button, so the footer stays in the same place on every screen.
export const QuestionActions: React.FC<{
  onPrevious?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  disabled?: boolean;
  busy?: boolean;
}> = ({ onPrevious, onContinue, continueLabel = 'Continue', disabled, busy }) => (
  <>
    <Box sx={{ display: { xs: 'block', sm: 'none' }, height: 88 }} />
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} sx={{
      position: { xs: 'fixed', sm: 'static' }, left: { xs: 0, sm: 'auto' }, right: { xs: 0, sm: 'auto' }, bottom: 0,
      zIndex: { xs: 1300, sm: 'auto' }, px: { xs: 2, sm: 0 }, pt: { xs: 1.25, sm: 0 },
      pb: { xs: 'max(14px, env(safe-area-inset-bottom))', sm: 0 },
      bgcolor: { xs: 'rgba(255,255,255,.96)', sm: 'transparent' },
      borderTop: { xs: '1px solid rgba(15,23,42,.10)', sm: 'none' },
      boxShadow: { xs: '0 -12px 32px rgba(15,23,42,.10)', sm: 'none' },
      backdropFilter: { xs: 'blur(16px)', sm: 'none' },
    }}>
      <Button onClick={onPrevious} disabled={!onPrevious || busy} startIcon={<ArrowBackRounded />}
        sx={{ ...launchActionButtonSx, px: { xs: 1.5, sm: 2 }, visibility: onPrevious ? 'visible' : 'hidden', fontWeight: 850 }}>
        Back
      </Button>
      <Button variant="contained" onClick={onContinue} disabled={!onContinue || disabled || busy}
        endIcon={!busy ? <ArrowForwardRounded /> : undefined}
        sx={{ ...launchActionButtonSx, minWidth: { xs: 164, sm: 180 }, visibility: onContinue ? 'visible' : 'hidden' }}>
        {busy ? 'Saving...' : continueLabel}
      </Button>
    </Stack>
  </>
);
